import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Prop,
  State,
} from "@stencil/core";

import notificationsStore from "@stores/notifications.store";
import comparisonStore from "@stores/product.comparison.store";
import cartStore from "@stores/cart.store";
import wishlistStore from "@stores/wishlist.store";
import inquiryStore from "@stores/inquiry.store";
import { isMobile } from "@globals/viewports";
import {
  AggregatorFlyoutProps,
  AggregatorListDataType,
} from "./f-aggregator-flyout-model";
import { findElement } from "@globals/helpers/shadow-dom-helpers";

@Component({
  tag: "f-aggregator-flyout",
  shadow: true,
})
export class AggregatorFlyout {
  @Element() el: HTMLFAggregatorFlyoutElement;

  @State() private aggregatorCounter = 0;

  /**
   * Specifies the list of flyouts (by tag name) to be aggregated in f-aggregator-flyout.
   * Notice that the order of items on the list is mapped in HTML. */

  @Prop({ mutable: true })
  aggregationList: AggregatorFlyoutProps["aggregationList"] = [
    // "f-inquiry-flyout",
    // "f-cart-flyout",
    "f-notification-flyout",
    "f-compare-flyout",
    // "f-wishlist-flyout",
  ];
  @Prop() buttonHiddenLabel: AggregatorFlyoutProps["buttonLabel"] =
    "Aggregator";
  @Prop() buttonTitle = this.buttonHiddenLabel;
  @Prop() inquiryLabel: AggregatorFlyoutProps["buttonLabel"] = "My inquiry";
  @Prop() inquiryIcon: AggregatorFlyoutProps["iconName"] = "post-it";
  @Prop() cartLabel: AggregatorFlyoutProps["buttonLabel"] = "My cart";
  @Prop() cartIcon: AggregatorFlyoutProps["iconName"] = "shoppingcart";
  @Prop() notificationsLabel: AggregatorFlyoutProps["buttonLabel"] =
    "My notifications";
  @Prop() notificationsIcon: AggregatorFlyoutProps["iconName"] =
    "notification-active";
  @Prop() comparisonLabel: AggregatorFlyoutProps["buttonLabel"] =
    "My comparison";
  @Prop() comparisonIcon: AggregatorFlyoutProps["iconName"] = "compare";
  @Prop() wishlistLabel: AggregatorFlyoutProps["buttonLabel"] = "My wishlist";
  @Prop() wishlistIcon: AggregatorFlyoutProps["iconName"] = "wishlist";
  @Prop() profileLabel: AggregatorFlyoutProps["buttonLabel"] = "My profile";
  @Prop() profileIcon: AggregatorFlyoutProps["iconName"] = "my-brand-frame";
  @Prop() quickAddLabel: AggregatorFlyoutProps["buttonLabel"] = "Quick Add";
  @Prop() quickAddIcon: AggregatorFlyoutProps["iconName"] = "shoppingcart-add";

  private aggregatorFlyoutWithButton: HTMLFFlyoutWithButtonElement;
  private otherFlyoutWithButton: HTMLFFlyoutWithButtonElement;
  private switchAggregatorTryTimeout = 200;

  private isAggregatorVisible = null;
  private aggregatorListData: AggregatorListDataType;

  @Event() openFlyout: EventEmitter;

  @Listen("resize", { target: "window" })
  toggleFlyoutButtonsVisibility(): void {
    if (isMobile()) {
      this.showAggregator();
    } else if (this.aggregatorFlyoutWithButton) {
      this.hideAggregator();
    }
  }

  componentWillLoad(): void {
    if (typeof this.aggregationList === "string") {
      this.aggregationList = JSON.parse(this.aggregationList);
    }
    this.setupAggregatorListData();
    this.aggregateByProps();
  }

  componentDidLoad(): void {
    this.toggleFlyoutButtonsVisibility();
  }

  private setupAggregatorListData(): void {
    this.aggregatorListData = {
      "f-inquiry-flyout": {
        label: this.inquiryLabel,
        icon: this.inquiryIcon,
        counter: 0,
      },
      "f-cart-flyout": {
        label: this.cartLabel,
        icon: this.cartIcon,
        counter: 0,
      },
      "f-notification-flyout": {
        label: this.notificationsLabel,
        icon: this.notificationsIcon,
        counter: 0,
      },
      "f-compare-flyout": {
        label: this.comparisonLabel,
        icon: this.comparisonIcon,
        counter: 0,
      },
      "f-wishlist-flyout": {
        label: this.wishlistLabel,
        icon: this.wishlistIcon,
        counter: 0,
      },
      "f-profile-flyout": {
        label: this.profileLabel,
        icon: this.profileIcon,
        counter: 0,
      },
      "f-quick-add-button": {
        label: this.quickAddLabel,
        icon: this.quickAddIcon,
        counter: 0,
      },
    };
  }

  private aggregateByProps(): void {
    this.aggregatorCounter = 0;

    if (Array.isArray(this.aggregationList)) {
      this.aggregationList.forEach((el) => {
        const flyoutEl = document.querySelector(el);
        let nextNumber = 0;

        if (Boolean(flyoutEl) && flyoutEl.getAttribute("badge-counter")) {
          nextNumber = parseInt(flyoutEl.getAttribute("badge-counter"));
        }
        this.aggregatorCounter += nextNumber;
      });
    }
  }

  private aggregateByStore(): void {
    this.aggregatorCounter = 0;
    if (Array.isArray(this.aggregationList)) {
      this.aggregationList.forEach((el) => {
        let currentCounter = 0;

        switch (el) {
          case "f-inquiry-flyout":
            currentCounter = parseInt(
              String(inquiryStore.inquiriesCart.totalItems)
            );
            break;
          case "f-cart-flyout":
            currentCounter = parseInt(String(cartStore.carts?.[0]?.totalItems));
            break;
          case "f-notification-flyout":
            currentCounter = parseInt(
              String(notificationsStore.totalUnreadNotifications)
            );
            break;
          case "f-compare-flyout":
            currentCounter = parseInt(
              String(comparisonStore.currentProductsQuantity)
            );
            break;
          case "f-wishlist-flyout":
            currentCounter = parseInt(
              String(wishlistStore.numberOfWishlistsItems)
            );
            break;
        }

        this.aggregatorListData[el].counter = currentCounter;
        this.aggregatorCounter += currentCounter;
      });
    }
  }

  private setAggregatorAsTrigger = (id) => {
    const otherFlyout = document.querySelector("#" + id);
    if (otherFlyout !== null) {
      this.aggregatorListData[id].defaultTrigger = (
        otherFlyout as HTMLFFlyoutElement
      ).attachedTrigger;
      (otherFlyout as HTMLFFlyoutElement).attachedTrigger = findElement(
        this.aggregatorFlyoutWithButton,
        "f-button-with-badge"
      );
    } else {
      setTimeout(() => {
        this.setAggregatorAsTrigger(id);
      }, this.switchAggregatorTryTimeout);
    }
  };

  private setDefaultTrigger = (id) => {
    const otherFlyout = document.querySelector("#" + id);
    if (otherFlyout !== null) {
      if (this.aggregatorListData[id].defaultTrigger) {
        (otherFlyout as HTMLFFlyoutElement).attachedTrigger =
          this.aggregatorListData[id].defaultTrigger;
      }
    } else {
      setTimeout(() => {
        this.setDefaultTrigger(id);
      }, this.switchAggregatorTryTimeout);
    }
  };

  private toggleFlyout = (id) => {
    const otherFlyout = document.querySelector(id);
    (this.otherFlyoutWithButton = findElement(
      otherFlyout,
      "f-flyout-with-button"
    )),
      this.aggregatorFlyoutWithButton.toggleFlyout();

    this.openFlyout.emit({
      id: id,
    });
  };

  private showAggregator = () => {
    if (
      this.isAggregatorVisible === false ||
      this.isAggregatorVisible === null
    ) {
      this.isAggregatorVisible = true;
      this.abortAgregatedFlyouts();
      this.switchAggregator("on");
      if (Array.isArray(this.aggregationList)) {
        this.aggregationList.forEach((item) => {
          this.setAggregatorAsTrigger(item);
        });
      }
    }
  };

  private hideAggregator = () => {
    if (
      this.isAggregatorVisible === true ||
      this.isAggregatorVisible === null
    ) {
      this.isAggregatorVisible = false;
      this.aggregatorFlyoutWithButton.closeFlyout(false);
      this.otherFlyoutWithButton?.closeFlyout(false);
      this.switchAggregator("off");
      if (Array.isArray(this.aggregationList)) {
        this.aggregationList.forEach((item) => {
          this.setDefaultTrigger(item);
        });
      }
    }
  };

  private switchAggregator = (state) => {
    this.el.style.display = state === "on" ? "" : "none";

    if (Array.isArray(this.aggregationList)) {
      this.aggregationList.forEach((item) => {
        const itemButton = document.querySelector<HTMLElement>(`${item}`);
        const target = findElement(itemButton, "f-button-with-badge");
        if (target !== null) {
          target.style.display = state === "on" ? "none" : "";
        } else {
          // if there is no target assume it has been not rendered yet so try again
          setTimeout(() => {
            this.switchAggregator(state);
          }, this.switchAggregatorTryTimeout);
        }
      });
    }
  };

  private abortAgregatedFlyouts = () => {
    if (Array.isArray(this.aggregationList)) {
      this.aggregationList.forEach((item) => {
        const flyout = document.querySelector(item);
        const flyoutWithButton = findElement(flyout, "f-flyout-with-button");

        flyoutWithButton?.closeFlyout(false);
      });
    }
  };

  render() {
    this.aggregateByStore();
    return (
      <Host data-test="aggregator-flyout">
        <f-flyout-with-button
          ref={(el) => (this.aggregatorFlyoutWithButton = el)}
          buttonBadgeLabel={this.aggregatorCounter}
          buttonIconName="options"
          flyoutDataTest="flyout-aggregator"
          buttonTitle={this.buttonTitle}
          variant="onlyContent"
        >
          <f-visually-hidden>{this.buttonHiddenLabel}</f-visually-hidden>
          <div slot="flyout-content">
            {Array.isArray(this.aggregationList)
              ? this.aggregationList.map((item) => {
                  return (
                    <f-button-with-badge
                      badgeLabel={this.aggregatorListData[item].counter}
                      iconName={this.aggregatorListData[item].icon}
                      onClick={() => this.toggleFlyout(item)}
                      data-test={`aggregator-item-${item}`}
                    >
                      {this.aggregatorListData[item].label}
                    </f-button-with-badge>
                  );
                })
              : ""}
          </div>
        </f-flyout-with-button>
      </Host>
    );
  }
}
